import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ShieldStar, UsersFour, FloppyDisk, ArrowLeft, Copy } from "@phosphor-icons/react";
import PermissionGrid from "@/components/PermissionGrid";

/*  Roles page — list of roles (left), permission grid editor (right).
    Click a role → its permissions load into the grid. Create / rename /
    delete via top toolbar. Editing the grid dirty-flags the Save button. */
export default function Roles() {
  const [roles, setRoles]         = useState([]);
  const [tree, setTree]           = useState([]);
  const [staffCount, setStaffCount] = useState({}); // role_id → count
  const [current, setCurrent]     = useState(null); // {id,name,description,permissions,dirty}
  const [selected, setSelected]   = useState(new Set());
  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });

  const loadAll = async () => {
    const [{ data: r }, { data: t }] = await Promise.all([
      http.get("/roles"),
      http.get("/permissions/tree"),
    ]);
    setRoles(r || []);
    setTree(t.tree || []);
    // count staff per role (single pass over all NAS)
    try {
      const { data: nasList } = await http.get("/nas");
      const counts = {};
      for (const n of nasList) {
        const { data: staff } = await http.get(`/nas/${n.id}/staff`);
        for (const s of staff || []) if (s.role_id) counts[s.role_id] = (counts[s.role_id] || 0) + 1;
      }
      setStaffCount(counts);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!current && roles.length) selectRole(roles[0]);
    // eslint-disable-next-line
  }, [roles]);

  const selectRole = (r) => {
    setCurrent({ ...r, dirty: false });
    setSelected(new Set(r.permissions || []));
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await http.post("/roles", { ...createForm, permissions: [] });
      toast.success(`Role "${data.name}" created`);
      setOpenCreate(false); setCreateForm({ name: "", description: "" });
      await loadAll(); selectRole(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };

  const save = async () => {
    if (!current) return;
    try {
      setSaving(true);
      const { data } = await http.patch(`/roles/${current.id}`, {
        name: current.name, description: current.description || "",
        permissions: [...selected],
      });
      toast.success(`Saved "${data.name}" (${data.permissions.length} perms)`);
      await loadAll();
      setCurrent({ ...data, dirty: false });
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!confirm(`Delete role "${r.name}"? Any employees assigned will fall back to individual permissions.`)) return;
    try {
      await http.delete(`/roles/${r.id}`);
      toast.success(`Role "${r.name}" removed`);
      await loadAll();
      setCurrent(null); setSelected(new Set());
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const clone = async (r) => {
    try {
      const { data } = await http.post(`/roles/${r.id}/clone`);
      toast.success(`Cloned "${r.name}" → "${data.name}"`);
      await loadAll();
      selectRole(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Clone failed"); }
  };

  const onGridChange = (newSet) => { setSelected(newSet); setCurrent((c) => c ? { ...c, dirty: true } : c); };

  const dirty = useMemo(() => {
    if (!current) return false;
    const before = new Set(current.permissions || []);
    if (before.size !== selected.size) return true;
    for (const p of before) if (!selected.has(p)) return true;
    return false;
  }, [current, selected]);

  return (
    <div className="space-y-4 fade-up" data-testid="roles-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Access Control</div>
          <h1 className="font-display font-bold text-4xl mt-1">Roles &amp; Permissions</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Bundle permissions into named roles (Super Admin, Support, Field Engineer…) and assign a role to each NAS employee. Employees inherit every permission from their role.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setOpenCreate(true)} className="rounded-sm h-9" data-testid="role-new">
            <Plus size={14} className="mr-1.5" /> New role
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Role list */}
        <aside className="border border-border bg-card overflow-hidden">
          <div className="px-3 py-2 border-b border-border text-xs uppercase font-mono flex items-center gap-2"><ShieldStar size={12} /> Roles ({roles.length})</div>
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
            {roles.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">No roles yet. Click "New role" to create your first.</div>}
            {roles.map((r) => (
              <div key={r.id} className={`border-b border-border last:border-b-0 flex items-stretch ${current?.id === r.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}`} data-testid={`role-item-${r.id}`}>
                <button
                  onClick={() => selectRole(r)}
                  className="flex-1 text-left px-3 py-3 hover:bg-accent/40 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm truncate">{r.name}</div>
                    <span className="text-[10px] font-mono text-muted-foreground inline-flex items-center gap-1"><UsersFour size={10} /> {staffCount[r.id] || 0}</span>
                  </div>
                  {r.description && <div className="text-[11px] text-muted-foreground mt-1 truncate">{r.description}</div>}
                  <div className="text-[10px] font-mono text-primary/80 mt-1">{(r.permissions || []).length} permissions</div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); clone(r); }}
                  className="px-3 border-l border-border text-muted-foreground hover:text-primary hover:bg-primary/5"
                  title="Clone role"
                  data-testid={`role-clone-${r.id}`}
                ><Copy size={13} /></button>
              </div>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <section>
          {!current ? (
            <div className="border border-border bg-card p-10 text-center text-sm text-muted-foreground">Select a role to edit its permissions, or create a new one.</div>
          ) : (
            <div className="space-y-4">
              <div className="border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="uppercase-label">Role name</Label>
                  <Input value={current.name} onChange={(e) => { setCurrent({ ...current, name: e.target.value, dirty: true }); }}
                         className="mt-1.5" data-testid="role-name-input" />
                </div>
                <div>
                  <Label className="uppercase-label">Description</Label>
                  <Textarea rows={2} value={current.description || ""} onChange={(e) => { setCurrent({ ...current, description: e.target.value, dirty: true }); }}
                            className="mt-1.5 text-sm" data-testid="role-desc-input" placeholder="What this role is for…" />
                </div>
              </div>
              <PermissionGrid
                tree={tree}
                value={selected}
                onChange={onGridChange}
                testidPrefix="role-perm"
              />
              <div className="flex items-center justify-center gap-3 pt-2">
                <Button onClick={save} disabled={saving || !dirty} data-testid="role-save"
                        className="rounded-sm h-10 px-8">
                  <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
                </Button>
                <Button variant="outline" onClick={() => remove(current)}
                        className="rounded-sm h-10 px-6 border-destructive text-destructive hover:bg-destructive/10"
                        data-testid="role-delete">
                  <Trash size={14} className="mr-2" /> Delete role
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Create modal */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="role-create-modal">
          <DialogHeader><DialogTitle className="font-display">New role</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="uppercase-label">Name</Label>
              <Input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                     className="mt-1.5" data-testid="role-create-name" autoFocus placeholder="e.g. Level-1 Support" />
            </div>
            <div>
              <Label className="uppercase-label">Description</Label>
              <Textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                        className="mt-1.5 text-sm" data-testid="role-create-desc" placeholder="One-line summary of what this role does" />
            </div>
            <div className="text-[10px] text-muted-foreground italic">You'll pick the permissions after creating the role.</div>
            <Button type="submit" className="w-full rounded-sm bg-primary hover:bg-primary/90" data-testid="role-create-submit">
              <Plus size={14} className="mr-1.5" /> Create role
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
